import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import useProfile from "common/hooks/useProfile";
import AssignmentMainContent, {
  AssignmentItem
} from "common/components/custom/assignment-clause-card/AssignmentMainContent";
import ConfirmationModal from "common/components/custom/confirmation-modal/ConfirmationModal";
import ViewExtractionConfigurationModal from "common/components/custom/assignment-clause-card/ViewExtractionConfigurationModal";
import AddConfigurationModal, {
  ConfigurationFormData,
  AssignmentField
} from "../add-new-configuration/AddConfigurationModal";
import {
  useGetAllConfigurationsQuery,
  useCreateConfigurationMutation,
  useUpdateConfigurationMutation,
  useDeleteConfigurationMutation
} from "../../../data/api/queries/configurations";
import { useUpdateExtractionConfigurationMutation } from "../../../data/api/commands/extractionConfigurationCommands";
import {
  ConfigurationItem,
  CreateConfigurationRequest,
  UpdateConfigurationRequest
} from "../../../data/services/config.service";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import "./AvailableConfiguration.scss";
import { Loading } from "modules/_shared/pages";
import { formattedDate } from "common/utils/date";
import { useAppDispatch, useAppSelector } from "common/redux/core";
import { setSelectedDocumentType } from "modules/_shared/redux/document-type";
import { fetchDocumentTypes } from "modules/_shared/redux/document-type/document-type.slice";

const AvailableConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const { displayName, mail, userPrincipalName, id } = useProfile();
  const dispatch = useAppDispatch();

  const userEmail = userPrincipalName || displayName || "";

  // Use global Redux state for document type
  const { selectedDocumentType, documentTypeOptions, loading } = useAppSelector(
    (state) => state.documentType
  );

  // Fetch document types when component mounts
  useEffect(() => {
    dispatch(fetchDocumentTypes() as any);
  }, [dispatch]);

  // Set default document type when component mounts if none is selected
  useEffect(() => {
    if (!selectedDocumentType && documentTypeOptions.length > 0) {
      // Set the first document type as default
      const firstDocType = documentTypeOptions[0]?.value;
      if (firstDocType) {
        dispatch(setSelectedDocumentType(firstDocType));
      }
    }
  }, [selectedDocumentType, documentTypeOptions, dispatch]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configurationList, setConfigurationList] = useState<
    ConfigurationItem[]
  >([]);
  const [editingConfiguration, setEditingConfiguration] =
    useState<ConfigurationItem | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] =
    useState<ConfigurationFormData | null>(null);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedConfigurationId, setSelectedConfigurationId] = useState<
    number | null
  >(null);
  const [selectedConfigurationData, setSelectedConfigurationData] =
    useState<ConfigurationItem | null>(null);

  const {
    data: configurationsData,
    isLoading,
    isError,
    error,
    refetch
  } = useGetAllConfigurationsQuery(undefined, {
    refetchOnReconnect: false,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0
  });

  const [deleteConfiguration] = useDeleteConfigurationMutation();
  const [updateExtractionConfiguration] =
    useUpdateExtractionConfigurationMutation();
  const [createConfiguration] = useCreateConfigurationMutation();
  const [updateConfiguration] = useUpdateConfigurationMutation();

  // Fallback data for when API is not available
  const mockConfigurations: ConfigurationItem[] = [
    {
      id: 1,
      doc_type_id: 1,
      config_name: "Assignment clause",
      extraction_model: "GPT-4",
      document_context: "Sample configuration",
      is_active: true,
      is_deleted: false,
      created_by_email: "admin@example.com",
      created_by: "admin",
      chat_enabled: false,
      created_on: new Date().toISOString(),
      updated_on: new Date().toISOString(),
      fields: [],
      // Legacy UI fields
      title: "Assignment clause",
      status: "Private",
      company: "admin",
      department: "admin",
      createdBy: "admin",
      createdOn: ""
    }
  ];

  // Get configurations data with fallback
  const configurations = React.useMemo(() => {
    if (isLoading && !configurationsData) {
      return [];
    }

    const transform = (apiData: any[]) => {
      const sortedData = [...apiData].sort((a, b) => {
        const dateA = new Date(a.created_time || 0).getTime();
        const dateB = new Date(b.created_time || 0).getTime();
        return dateA - dateB;
      });
      // const documentTypeName = getDocumentTypeName(apiItem.doc_type_id);
      return sortedData.map((apiItem: any, index: number) => {
        const isCurrentUserCreator =
          apiItem.created_by === displayName ||
          apiItem.created_by === userEmail ||
          apiItem.created_by === userPrincipalName ||
          apiItem.created_by === id?.toString();

        const configKey = `config_${apiItem.id}_document_type`;
        const storedDocumentType = localStorage.getItem(configKey);
        const documentType = storedDocumentType || apiItem.document_type;

        // Apply "Others" categorization logic
        // const standardDocTypes = ["Engineering", "Contract", "Invoice"];
        // const isStandardDocType = standardDocTypes.includes(documentType || "");
        // const categorizedDocumentType = isStandardDocType
        //   ? documentType || "Engineering"
        //   : "Others";

        return {
          // Required fields from new API response structure
          id: apiItem.id || index + 1,
          doc_type_id: apiItem.doc_type_id || 0,
          config_name:
            apiItem.config_name || `Configuration ${apiItem.id || index + 1}`,
          extraction_model: apiItem.extraction_model || "",
          document_context: apiItem.document_context || "",
          is_active: apiItem.is_active ?? true,
          is_deleted: apiItem.is_deleted ?? false,
          created_by_email: apiItem.created_by_email || "",
          created_by: apiItem.created_by || "",
          chat_enabled: apiItem.chat_enabled ?? false,
          created_on: apiItem.created_on || new Date().toISOString(),
          updated_on: apiItem.updated_on || new Date().toISOString(),
          fields: apiItem.fields || [],

          // Legacy/UI fields for backward compatibility (required for AssignmentItem interface)
          title:
            apiItem.config_name || `Configuration ${apiItem.id || index + 1}`,
          status: apiItem.data_exposer === "private" ? "Private" : "Private",
          company: apiItem.company_name || "",
          department: apiItem.department || "",
          createdBy: isCurrentUserCreator
            ? displayName || ""
            : apiItem.created_by || "",
          createdOn: apiItem.created_on
            ? formattedDate(apiItem.created_on)
            : "",
          documentType: apiItem?.doc_type_id?.toString() || "",
          // Store additional API fields for backward compatibility
          config_payload: apiItem.config_payload,
          document_type: documentType,
          company_name: apiItem.company_name,
          data_exposer: apiItem.data_exposer,
          field_context: apiItem.field_context,
          chat_exposure: apiItem.chat_enabled,
          clause_extraction: apiItem.clause_extraction,
          summarization_fields: apiItem.summarization_fields,
          group_by_field: apiItem.group_by_field,
          batch_process_exposure: apiItem.batch_process_exposure,
          sharepoint_url: apiItem.sharepoint_url,
          created_time: apiItem.created_time
        } as ConfigurationItem;
      });
    };

    if (configurationsData && Array.isArray(configurationsData)) {
      return transform(configurationsData);
    }

    if (configurationsData && Array.isArray((configurationsData as any).data)) {
      return transform((configurationsData as any).data);
    }

    if (isError) {
      return mockConfigurations;
    }

    return mockConfigurations;
  }, [
    isLoading,
    isError,
    configurationsData,
    displayName,
    userEmail,
    userPrincipalName,
    id
  ]);

  useEffect(() => {
    // Set all configurations - filtering will happen in AssignmentMainContent
    setConfigurationList(configurations);
  }, [configurations, selectedDocumentType]);

  const convertToFormData = useCallback(
    (config: ConfigurationItem): ConfigurationFormData => {
      const assignmentFields: AssignmentField[] = [];

      // if (
      //   config.field_context ||
      //   config.clause_extraction ||
      //   config.summarization_fields
      // ) {
      //   const fieldNames =
      //     config.summarization_fields
      //       ?.split(",")
      //       .map((name) => name.trim())
      //       .filter(Boolean) || [];

      //   const fieldContextMap = new Map<string, string>();
      //   if (config.field_context) {
      //     try {
      //       // Try parsing as JSON first (new format)
      //       const parsedContext = JSON.parse(config.field_context);
      //       if (typeof parsedContext === "object" && parsedContext !== null) {
      //         Object.entries(parsedContext).forEach(([fieldName, context]) => {
      //           fieldContextMap.set(fieldName, String(context));
      //         });
      //       }
      //     } catch {
      //       // Fallback to old format parsing (semicolon and colon separated)
      //       config.field_context.split(";").forEach((entry) => {
      //         const [fieldName, ...contextParts] = entry.split(":");
      //         if (fieldName && contextParts.length > 0) {
      //           fieldContextMap.set(
      //             fieldName.trim(),
      //             contextParts.join(":").trim()
      //           );
      //         }
      //       });
      //     }
      //   }

      //   const validationRuleMap = new Map<string, string>();
      //   if (config.clause_extraction) {
      //     config.clause_extraction.split(";").forEach((entry) => {
      //       const [fieldName, ...ruleParts] = entry.split(":");
      //       if (fieldName && ruleParts.length > 0) {
      //         validationRuleMap.set(
      //           fieldName.trim(),
      //           ruleParts.join(":").trim()
      //         );
      //       }
      //     });
      //   }

      //   fieldNames.forEach((fieldName, index) => {
      //     assignmentFields.push({
      //       id: (index + 1).toString(),
      //       fieldName: fieldName,
      //       fieldContext: fieldContextMap.get(fieldName) || "",
      //       validationRule: validationRuleMap.get(fieldName) || ""
      //     });
      //   });
      // }

      if (assignmentFields.length === 0) {
        assignmentFields.push({
          id: "1",
          fieldName: "",
          fieldContext: "",
          validationRule: "",
          qualityRule: ""
        });
      }

      const formData = {
        configurationName: config.config_name || config.title || "",
        documentType: config.documentType?.toString() || "",
        flowDescription: config.document_context || "",
        selectModel: config.extraction_model || "",
        chatEnabled: config.chat_exposure || false,
        assignmentFields: assignmentFields,
        fields: config.fields
      };

      return formData;
    },
    []
  );

  const handleEdit = useCallback(
    async (configId: number) => {
      try {
        const configToEdit = (configurations as ConfigurationItem[]).find(
          (config: ConfigurationItem) => config.id === configId
        );

        if (configToEdit) {
          const convertedData = convertToFormData(configToEdit);

          setEditFormData(convertedData);
          setEditingConfiguration(configToEdit);
          setIsEditMode(true);
          setIsModalOpen(true);
        }
      } catch (err) {
        console.error("Error editing configuration:", err);
        ToastMessageUtil.show(
          "Error loading configuration for editing",
          ToastVariant.ERROR
        );
      }
    },
    [configurations, convertToFormData]
  );

  const handleDelete = useCallback(
    async (configId: number) => {
      // Find the configuration to get its name
      const configurationToDelete = (
        configurations as ConfigurationItem[]
      ).find((config: ConfigurationItem) => config.id === configId);
      const configName =
        configurationToDelete?.title ||
        configurationToDelete?.config_name ||
        `Configuration ${configId}`;

      // Set the configuration to delete and show modal
      setConfigToDelete({ id: configId, name: configName });
      setDeleteModalOpen(true);
    },
    [configurations]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!configToDelete) return;

    setIsDeleting(true);
    try {
      await deleteConfiguration(configToDelete.id).unwrap();
      refetch();
      ToastMessageUtil.show(
        "Configuration deleted successfully!",
        ToastVariant.SUCCESS
      );
      setDeleteModalOpen(false);
      setConfigToDelete(null);
    } catch (e) {
      ToastMessageUtil.show(
        `Error deleting configuration: ${e}`,
        ToastVariant.ERROR
      );
    } finally {
      setIsDeleting(false);
    }
  }, [configToDelete, deleteConfiguration, refetch]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setConfigToDelete(null);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingConfiguration(null);
    setEditFormData(null);
  }, []);

  const handleNewConfiguration = useCallback(() => {
    setIsEditMode(false);
    setEditingConfiguration(null);
    setEditFormData(null);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback(
    (configId: number) => {
      const configToView = (configurations as ConfigurationItem[]).find(
        (config: ConfigurationItem) => config.id === configId
      );

      if (configToView) {
        setSelectedConfigurationId(configId);
        setSelectedConfigurationData(configToView);
        setViewModalOpen(true);
      }
    },
    [configurations]
  );

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedConfigurationId(null);
    setSelectedConfigurationData(null);
  }, []);

  const handleCreateFlow = useCallback(
    (configId: number) => {
      // Find the configuration that was clicked
      const selectedConfig = (configurations as ConfigurationItem[]).find(
        (config: ConfigurationItem) => config.id === configId
      );

      if (selectedConfig) {
        const configName =
          selectedConfig.config_name || selectedConfig.title || "";
        const documentType =
          selectedConfig.documentType ||
          selectedConfig.doc_type_id?.toString() ||
          "";

        // Navigate to configure-ops page with configuration data
        const url = `/configure-ops?configId=${configId}&configName=${encodeURIComponent(
          configName
        )}&documentType=${encodeURIComponent(documentType)}`;
        navigate(url);
      } else {
        console.warn("Selected configuration not found! ConfigId:", configId);
        // Fallback navigation without parameters
        navigate("/configure-ops");
      }
    },
    [navigate, configurations]
  );

  const handleDocumentTypeChange = useCallback(
    (documentType: string) => {
      dispatch(setSelectedDocumentType(documentType));
    },
    [dispatch]
  );

  const handleSaveConfiguration = useCallback(
    async (data: ConfigurationFormData) => {
      try {
        // Map user/display names to IDs, example: created_by_id as 1 for now
        const createdById = 1; // Should be replaced with actual user ID mapping
        const mapFieldsPayload = (
          fields: typeof data.assignmentFields,
          configId: number
        ) =>
          fields.map((field, index) => {
            return {
              config_id: configId,
              field_name: field.fieldName,
              field_key: field.fieldContext,
              field_description: field.fieldDescription || "",
              data_type: "String",
              validation_rules: field.validationRule || "",
              quality_rules: field.qualityRule || "",
              ui_position_order: field.ui_position_order || index + 1,
              is_active: true,
              is_deleted: false
            };
          });

        if (isEditMode && editingConfiguration) {
          // Build payload for update
          const updateRequest = {
            id: editingConfiguration.id,
            doc_type_id: parseInt(data.documentType) || "",
            // Adjust as per logic if needed
            config_name: data.configurationName,
            extraction_model: data.selectModel || "",
            document_context: data.flowDescription || "",
            chat_enabled: data?.chatEnabled || false,
            is_active: true,
            is_deleted: false,
            created_by_id: createdById,
            created_on: "2025-11-20T11:10:42.449Z",
            updated_on: "2025-11-20T11:10:42.449Z",
            fields: mapFieldsPayload(
              data.assignmentFields,
              editingConfiguration.id
            )
          };

          await updateConfiguration({
            configId: 1, // existing configuration id
            configData: updateRequest // full update request object
          }).unwrap();

          ToastMessageUtil.show(
            "Configuration updated successfully!",
            ToastVariant.SUCCESS
          );
          handleModalClose();
        } else {
          // Build payload for create
          const createRequest = {
            doc_type_id: parseInt(data.documentType) || 0,
            config_name: data.configurationName || "",
            extraction_model: data.selectModel || "",
            document_context: data.flowDescription || "",
            is_active: true,
            is_deleted: false,
            created_by_email: userPrincipalName || "",
            created_by: displayName || userPrincipalName || "",
            chat_enabled: data?.chatEnabled || false,
            fields: mapFieldsPayload(data.assignmentFields, 0) // config_id 0 for new config
          };

          const headers = { "X-User-Email": userPrincipalName || "" };

          await createConfiguration({
            configData: createRequest,
            headers: headers
          }).unwrap();
          refetch();

          ToastMessageUtil.show(
            "Configuration created successfully!",
            ToastVariant.SUCCESS
          );
          handleModalClose();
        }
      } catch (errors: any) {
        let errorMessage = `Error ${
          isEditMode ? "updating" : "creating"
        } configuration`;
        if (errors?.data?.message) {
          errorMessage += `: ${errors.data.message}`;
        } else if (errors?.message) {
          errorMessage += `: ${errors.message}`;
        } else if (errors?.data?.error) {
          errorMessage += `: ${errors.data.error}`;
        } else {
          errorMessage += `. Check console for details.`;
        }
        ToastMessageUtil.show(errorMessage, ToastVariant.ERROR);
      }
    },
    [
      createConfiguration,
      updateConfiguration,
      displayName,
      userEmail,
      userPrincipalName,
      isEditMode,
      editingConfiguration,
      handleModalClose,
      refetch
    ]
  );

  // Loading state
  if (isLoading && !configurationsData) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="available-configuration-container">
          <HubHeader
            className="header"
            title="Configuration Hub"
            showBackButton={true}
          />
          <div className="available-configuration-content">
            <div className="loading-container">
              <Loading />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="available-configuration-container">
        <HubHeader
          className="header"
          title="Configuration Hub"
          showBackButton={true}
          backRoute="/configure"
        />

        {/* Main Content */}
        <div className="available-configuration-content">
          <AssignmentMainContent
            assignments={configurations as any[]}
            configurationList={configurationList as any[]}
            setConfigurationList={setConfigurationList as any}
            showDropdown={false}
            showNewConfigButton={true}
            newConfigButtonText="New configuration"
            onNewConfigClick={handleNewConfiguration}
            cardsClickable={false}
            showActions={true}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            // onCreateFlow={handleCreateFlow}
            // showCreateFlowBtn={true}
            showDocumentTypeFilter={true}
            onDocumentTypeChange={handleDocumentTypeChange}
            headerTitle="Available Extraction Configurations"
            className="available-configuration-main-content"
          />
        </div>
      </div>

      {/* Add Configuration Modal */}
      <AddConfigurationModal
        key={`${isEditMode ? "edit" : "add"}-${
          editingConfiguration?.id || "new"
        }`}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveConfiguration}
        existingConfigNames={configurations.map(
          (config) => config.title || config.config_name || ""
        )}
        isEditMode={isEditMode}
        initialData={editFormData || undefined}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title="Delete Extraction Configuration"
        message={`Are you sure you want to delete "${configToDelete?.name}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeleting}
      />

      {/* View Extraction Configuration Modal */}
      <ViewExtractionConfigurationModal
        isOpen={viewModalOpen}
        onClose={handleCloseViewModal}
        configurationId={selectedConfigurationId}
        configurationData={selectedConfigurationData}
      />
    </>
  );
};

export default AvailableConfiguration;
